import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import { useEffect, useMemo, useState } from "react";
import instance from "../../utils/api_instance.util";
import Button from "../../components/button/Button";
import { formatDateTime } from "../../utils/formatDate";
import DateInput from "../../components/form/DateInput";

const Activitylogs = () => {

  // ✅ NEW: Activity logs state (local, no slice change)
  const [activityRows, setActivityRows] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Pagination State (same as Game)
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);

  // Search State (same as Game)
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Debounce searchText (same as Game)
  const debouncedSearch = useMemo(() => {
    let t;
    return {
      trigger: (cb) => {
        clearTimeout(t);
        t = setTimeout(cb, 400);
        return () => clearTimeout(t);
      },
    };
  }, []);

  // ✅ API call for activity logs (same flow as Game list)
  const fetchActivityLogs = async ({
    limit,
    offset,
    searchText,
    fromDate,
    toDate,
  }) => {
    setActivityLoading(true);
    try {
      const payload = {
        limit,
        offset,
        searchText,
        from: fromDate || null,
        to: toDate || null,
      };
      const { data } = await instance.post(
        "/api/v1/admin/dashboard/activity-log/list",
        payload,
      );

      const rows = data?.response?.rows || [];
      setActivityRows(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error("Activity logs fetch failed:", e);
      setActivityRows([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // Fetch list with pagination + search (debounced) — same as Game
  useEffect(() => {
    const run = () => {
      fetchActivityLogs({ offset, limit, searchText, fromDate, toDate });
    };
    return debouncedSearch.trigger(run);
  }, [offset, limit, searchText, fromDate, toDate, debouncedSearch]);

  // Reset to first page when search or limit changes (same as Game)
  useEffect(() => {
    setOffset(0);
  }, [searchText, limit, fromDate, toDate]);

  const substringValue = (value) =>
    value ? String(value).substring(0, 100) : "";

  // Pagination handlers (same as Game)
  const nextPage = () => {
    if (activityRows.length === limit) setOffset((prev) => prev + limit);
  };
  const prevPage = () => {
    setOffset((prev) => Math.max(0, prev - limit));
  };

  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <main id="main" className="main">
      <Breadcrumb page="Activity Logs" />

      {/* ✅ NEW TABLE (exact same layout style as Game component you sent) */}
      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body overflow-x-scroll">
                {/* Heading + Search (side-by-side) */}
                <div className="d-flex w-100 align-items-center gap-2 flex-wrap">
                  <h5 className="card-title mb-0 me-2">Activity Logs</h5>
                </div>
<div className="d-flex flex-wrap gap-2 align-items-center mb-3">
  {/* Search */}
  <input
    type="text"
    className="form-control form-control-sm"
    placeholder="Search activity..."
    style={{ width: 220 }}
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
  />

  {/* From Date */}
  <DateInput pastOnly
    style={{ width: 160 }}
    value={fromDate}
    onChange={setFromDate}
  />

  {/* To Date */}
  <DateInput pastOnly
    style={{ width: 160 }}
    value={toDate}
    onChange={setToDate}
  />

  {/* Clear Button */}
  <Button
    className="btn btn-sm btn-outline-secondary"
    onClick={() => {
      setFromDate("");
      setToDate("");
    }}
  >
    Clear
  </Button>
</div>

                <table className="table table-bordered mt-3">
                  <thead>
                    <tr>
                      <th className="text-center fs-7">User</th>
                      <th className="text-center fs-7">Email</th>
                      <th className="text-center fs-7">Activity</th>
                      <th className="text-center fs-7">Created At</th>
                    </tr>
                  </thead>

                  <tbody>
                    {activityLoading ? (
                      <tr>
                        <td colSpan="5" className="text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : activityRows?.length ? (
                      activityRows.map((row, i) => (
                        <tr key={`activity-${row?.id || i}`}>
                          <td className="text-center fs-7">
                            {row?.user_name || null}
                          </td>
                          <td className="text-center fs-7">
                            {row?.user_username || null}
                          </td>
                          <td
                            style={{ maxWidth: 300 }}
                            className="text-justify fs-7"
                          >
                            {substringValue(row?.activityMessage)}
                          </td>
                          <td className="text-center fs-7">
                            {formatDateTime(row?.createdat)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No Activity Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination + Limit selector (same as Game) */}
                <div className="d-flex justify-content-end align-items-center mt-3 gap-2 flex-wrap">
                  {/* Limit dropdown */}
                  <div className="d-inline-flex align-items-center gap-2">
                    <span className="fs-7">Show</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 90 }}
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="fs-7">per page</span>
                  </div>

                  {/* Spacer */}
                  <div className="flex-grow-1" />

                  {/* Pagination buttons with page number */}
                  <Button
                    className="btn btn-outline-primary btn-sm"
                    onClick={prevPage}
                    disabled={offset === 0}
                  >
                    Previous
                  </Button>

                  <span className="px-2 fs-7">Page {currentPage}</span>

                  <Button
                    className="btn btn-outline-primary btn-sm"
                    onClick={nextPage}
                    disabled={
                      activityRows.length < limit || activityRows.length === 0
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Activitylogs;
